import {customElement, property} from 'lit/decorators.js';
import {CSSResult, html, LitElement, nothing, unsafeCSS} from 'lit';
import {consume} from '@lit/context';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {Messages, ProductIdentifierContainer} from '../../../@types/wishlist.js';
import {ProductEventAggregate} from '../../../scripts/product-events/product-event-aggregate.js';
import styles from './event-item.scss';
import {sharedCss} from '../../../scripts/style-bundle/shared.js';
import {trackUpdatesClick, trackUpdatesListView} from '../../../scripts/service/cube-tracking-layer.js';

@customElement('ents-event-item')
export class EventLayer extends LitElement {
  @consume({context: i18nContext})
  private messages!: Messages;

  @property({type: Object, reflect: false})
  private productEventAggregate!: ProductEventAggregate;

  @property({type: Number, attribute: 'position'})
  private position!: number;

  @property({type: Number, attribute: 'count'}) private countListItems!: number;

  @property({type: Boolean, attribute: 'logged-in'}) protected loggedIn: boolean = false;

  @property({type: Boolean, attribute: 'already-seen'})
  protected alreadySeen: boolean = false;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  connectedCallback() {
    super.connectedCallback();
    this.injectInterceptionObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  render() {
    const productIdentifier = this.productEventAggregate.productIdentifier.id;
    return html`
      <a
        href="#ents-wishlist-product-${productIdentifier}"
        @click="${(e: Event) => this.handleEventItemClick(e, productIdentifier)}"
        class="ents-wishlist-event-item__grid"
        data-test-ents-event-item-link="${productIdentifier}"
      >
        <div class="ents-wishlist-event-item__image-content">
          ${this.renderImage()}
        </div>
        <div class="ents-wishlist-event-item__text-content">
          ${this.renderNames()}
          ${this.renderColor()}
          ${this.renderSize()}
          ${this.renderEvents()}
        </div>
      </a>
    `;
  }

  private handleEventItemClick(e: Event, productIdentifier: string) {
    trackUpdatesClick(this.productEventAggregate, this.position, this.countListItems, this.loggedIn);
    this.dispatchEvent(new CustomEvent<ProductIdentifierContainer>('ents-wishlist-event-item-clicked', {
      detail: {
        identifier: productIdentifier
      },
      composed: true,
      bubbles: true,
    }));
  }

  private renderImage() {
    return html`
      <ents-wishlist-image
        data-test-ents-wishlist-picture
        image-url="${this.productEventAggregate.imageUrl}"
        sizes="(min-width: 760px) 101px, (min-width: 560px) 152px, 101px"
        alt-text="${this.productEventAggregate.brandName} ${this.productEventAggregate.productName}"
        ?is-beauty="${this.productEventAggregate.isBeauty}"
        brand-code="${this.productEventAggregate.brandCode}"
      ></ents-wishlist-image>
    `;
  }

  private renderNames() {
    return html`
      <div class="ents-wishlist-event-item__brand-name" data-test-ents-event-item-brand>
        ${this.productEventAggregate.brandName}
      </div>
      <div class="ents-wishlist-event-item__name" data-test-ents-event-item-name>
        ${this.productEventAggregate.productName}
      </div>
    `;
  }

  private renderColor() {
    return this.productEventAggregate.colorName ? html`
      <div class="ents-wishlist-event-item__color">
        ${this.messages['ents_wishlist_product_color']}
        ${this.productEventAggregate.colorName}
      </div>
    ` : nothing;
  }

  private renderSize() {
    const selectedSizeOrQuantity = this.productEventAggregate.sizeOrQuantity;
    return selectedSizeOrQuantity ? html`
      <div class="ents-wishlist-event-item__size" aria-live="polite">
        <span>${this.messages['ents_wishlist_product_size']}</span>
        <span data-test-ents-selected-size>${selectedSizeOrQuantity}</span>
      </div>
    ` : nothing;
  }

  private renderEvents() {
    return html`
      <div class="ents-wishlist-event-item__badges">
        ${this.productEventAggregate.events.map(event =>
          html`
            <div
              class="bdk-badge-system ents-wishlist-event-item__badge"
              variant="${this.getVariantForEvent(event)}"
              data-test-ents-event-item-event
            >
              ${this.messages[`ents_wishlist_event_layer_type_${event}`] ?? ''}
            </div>
          `)}
      </div>`;
  }

  private getVariantForEvent(event: string): string {
    const variantMap: Record<string, string> = {
      product_campaign_started: 'coal',
      product_back_in_stock: 'positive',
      product_shortage: 'neutral',
      product_price_reduced: 'sale',
    };

    return variantMap[event.toLowerCase()] || '';
  }

  private injectInterceptionObserver() {
    const options: IntersectionObserverInit = {
      threshold: 0.5,
    };

    const callback: IntersectionObserverCallback = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          trackUpdatesListView(
            this.productEventAggregate,
            this.position,
            this.countListItems,
            this.loggedIn,
            this.alreadySeen
          );
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);
    observer.observe(this);
  }
}
